import tutos from '../../components/ConventionPage/__fixtures__/tutos-proposition.json';

// Local mock of the acknowledgement API of HIA-BUG-20260929-7 (REACT_APP_MOCK=true only). The examples are the
// 27 examples verified against the production engine (_tracker/cards/rGbcXIXl/tutos-proposition.json), with
// fictitious markers. The mock family is chosen by the ack token of the URL: /ack-of-receipt/fr/demo-<family>
// (default alpha-num-1), /ack-of-receipt/fr/demo-error-<key> answers an ack-start refusal.

// Structured parameters as the engine returns them (conventionParameters of ack-start), per mock family.
const PARAMETERS = {
    nova: { challengeLength: 16, blocks: [4, 4, 4, 4], alphabet: 'ALPHANUMERIC', answerLength: 0 },
    Nulle: { challengeLength: 6, blocks: [3, 3], alphabet: 'DIGITS', answerLength: 6 },
    'alpha-bet-1': { challengeLength: 16, blocks: [4, 4, 4, 4], alphabet: 'ALPHANUMERIC', answerLength: 4, marker: 'M', markerCaseSensitive: false, markerMaxIndex: 11 },
    'alpha-num-1': { challengeLength: 16, blocks: [4, 4, 4, 4], alphabet: 'ALPHANUMERIC', answerLength: 4, marker: '9', markerCaseSensitive: true, markerMaxIndex: 11 },
    Zeta1: { challengeLength: 60, blocks: [60], alphabet: 'ALPHANUMERIC', answerLength: 6, keyword: 'BOLIRA' },
    Zeta2: { challengeLength: 60, blocks: [60], alphabet: 'ALPHANUMERIC', answerLength: 6, keyword: 'SUNOKE' },
    Zeta4: { challengeLength: 60, blocks: [60], alphabet: 'ALPHANUMERIC', answerLength: 4, keyword: 'TIVO' },
    Zeta5: { challengeLength: 60, blocks: [60], alphabet: 'ALPHANUMERIC', answerLength: 5, keyword: 'RAPEL' },
    Pattern1: { challengeLength: 40, blocks: [40], alphabet: 'ALPHANUMERIC', answerLength: 4, marker: 'K', markerCaseSensitive: false, occurrence: 2, pattern: ['DIGIT', 'LOWER', 'UPPER_OR_DIGIT', 'UPPER'] },
    Pattern2: { challengeLength: 40, blocks: [40], alphabet: 'ALPHANUMERIC', answerLength: 4, marker: 'r', markerCaseSensitive: true, occurrence: 2, pattern: ['LOWER_OR_DIGIT', 'DIGIT', 'UPPER', 'LOWER'] },
};

export const MOCK_FAMILIES = Object.keys(PARAMETERS);
export const DEFAULT_FAMILY = 'alpha-num-1';

export const familyOf = (ackToken) => {
    const name = String(ackToken || '').replace(/^demo-/, '');
    return PARAMETERS[name] ? name : DEFAULT_FAMILY;
};

const entryOf = (family) => tutos.find((entry) => entry.conventionFamily.name === family);

// Keyword conventions: one engine step per letter, TAKE_NEXT or TAKE_PREVIOUS (as the engine KeywordSolver does).
const keywordSteps = (example, lang) => (example.why[lang] || example.why.fr).split(' ; ').map((text) => ({
    code: /avant|before/.test(text) ? 'TAKE_PREVIOUS' : 'TAKE_NEXT',
    text,
}));

// A verified example of the tracker file, converted to the engine Solution model (test helper of the spec-tech §6.1).
export const toSolution = (example, lang, family) => {
    const highlights = [];
    if (example.highlight.key.length) {
        highlights.push({ role: 'MARKER', target: { kind: 'TEXT_INDEXES', indexes: example.highlight.key } });
    }
    highlights.push({ role: 'ANSWER', target: { kind: 'TEXT_INDEXES', indexes: example.highlight.answer } });
    return {
        answerRequired: true,
        answer: { kind: 'TEXT', value: example.expected },
        highlights,
        steps: family && PARAMETERS[family] && PARAMETERS[family].keyword
            ? keywordSteps(example, lang)
            : [{ code: example.highlight.key.length ? 'FIND_MARKER' : 'COPY_ALL', text: example.why[lang] || example.why.fr }],
    };
};

export const buildAckStart = (ackToken, lang) => {
    const family = familyOf(ackToken);
    const entry = entryOf(family);
    return {
        securityLevel: entry.conventionFamily.level,
        convention: lang === 'en' ? entry.explanation.ruleEn || entry.convention : entry.convention,
        mnemonique: entry.mnemonique,
        timer: null,
        sessionTimeOut: new Date(Date.now() + 1800 * 1000).toISOString(),
        langKey: lang,
        conventionId: `mock-${family}`,
        conventionParameters: { family, ...PARAMETERS[family] },
        ackSettings: { linkValiditySeconds: 259200, maxOpenings: 2, sessionDurationSeconds: 1800 },
    };
};

let exerciseSeq = 0;

export const buildPractice = (ackToken, lang, mode) => {
    const family = familyOf(ackToken);
    const examples = entryOf(family).examples;
    if (!examples.length) return { practiceAvailable: false, items: [] };
    if (mode === 'EXAMPLE') {
        return {
            practiceAvailable: true,
            items: examples.map((example, index) => ({
                practiceId: `ex-${index}`,
                contentType: 'text/plain',
                content: example.challenge,
                solution: toSolution(example, lang, family),
            })),
        };
    }
    const index = exerciseSeq % examples.length;
    exerciseSeq += 1;
    return { practiceAvailable: true, items: [{ practiceId: `pr-${index}`, contentType: 'text/plain', content: examples[index].challenge }] };
};

// Same verdicts as the engine: exact -> CORRECT, same characters with another case -> CASE_ONLY, else WRONG.
export const buildCheck = (ackToken, lang, { practiceId, answer }) => {
    const family = familyOf(ackToken);
    const examples = entryOf(family).examples;
    const example = examples[Number(String(practiceId).split('-')[1])];
    if (!example) return null;
    const expected = example.expected;
    let verdict = 'WRONG';
    if (answer === expected) verdict = 'CORRECT';
    else if (typeof answer === 'string' && answer.toLowerCase() === expected.toLowerCase()) verdict = 'CASE_ONLY';
    return { verdict, solution: toSolution(example, lang, family) };
};
